import httpRequest from "../httpRequest";
import type {
  CommonCompanyOption,
  ProfilePictureUploadResponse,
  TagsListType,
  UserSettings,
  ReportUsersType,
  UOMQuantityType,
  SiteAllItem,
  SiteDropdownItem,
  SystemConfigurationResponse,
  UserSettingsResponse,
  MeterGroupItem,
  LastReadingItem,
  LastReadingRequest,
  MeterStatusData,
} from "./type/common.type";
import { StandardResponse } from "../response.model";
import { API_ENDPOINTS } from "@/shared/constants";
import { MeterChannelResponse } from "@/features/PlatformConfiguration/types/PlatformConfiguration.type";
import { SideMenuResponse } from "@/shared/type/SideMenu.type";

const commonServices = {
  async getSystemConfiguration(): Promise<SystemConfigurationResponse> {
    return httpRequest.get(API_ENDPOINTS.common.systemConfiguration);
  },

  async getUserSettings(): Promise<UserSettingsResponse> {
    return httpRequest.get(API_ENDPOINTS.common.userSettings);
  },

  async saveUserSettings(
    payload: UserSettings["settings"],
  ): Promise<StandardResponse<boolean>> {
    return httpRequest.post<boolean, UserSettings["settings"]>(
      API_ENDPOINTS.common.userSettings,
      payload,
    );
  },

  async uploadProfilePicture(
    file: File,
  ): Promise<StandardResponse<ProfilePictureUploadResponse>> {
    const formData = new FormData();
    formData.append("file", file);

    return httpRequest.post<ProfilePictureUploadResponse, FormData>(
      API_ENDPOINTS.common.uploadProfile,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },

  async fetchMeterStatus(
    encryptedMeterId: string,
  ): Promise<StandardResponse<MeterStatusData>> {
    return await httpRequest.get<MeterStatusData>(
      API_ENDPOINTS.management.meter.currentStatus(encryptedMeterId),
    );
  },

  async fetchLastReading(
    payload: LastReadingRequest,
  ): Promise<StandardResponse<LastReadingItem[]>> {
    return await httpRequest.post<LastReadingItem[], LastReadingRequest>(
      API_ENDPOINTS.management.meter.lastReading,
      payload,
    );
  },
};

export default commonServices;

export const getAllTags = async (): Promise<
  StandardResponse<TagsListType[]>
> => {
  return await httpRequest.get<TagsListType[]>(
    API_ENDPOINTS.reports.getAllTags,
  );
};

export const getNem12ReportUsers = async (): Promise<
  StandardResponse<ReportUsersType[]>
> => {
  return await httpRequest.get<ReportUsersType[]>(
    API_ENDPOINTS.reports.dataValidation.users,
  );
};

export const getMeterAggregationQuantityNames = async (): Promise<
  StandardResponse<string[]>
> => {
  return await httpRequest.get<string[]>(
    API_ENDPOINTS.common.meterAggregationQuantityNames,
  );
};

export const getMeterAggregationUomByQuantity = async (
  quantityType: string,
): Promise<StandardResponse<UOMQuantityType[]>> => {
  return await httpRequest.get<UOMQuantityType[]>(
    API_ENDPOINTS.common.meterAggregationUomByQuantity(quantityType),
  );
};

export const getMeterTypes = async (): Promise<StandardResponse<string[]>> => {
  return await httpRequest.get<string[]>(API_ENDPOINTS.common.meterType);
};

export const getMeterChannels = async (): Promise<
  StandardResponse<MeterChannelResponse[]>
> => {
  return await httpRequest.get<MeterChannelResponse[]>(
    API_ENDPOINTS.platformConfig.channelsOptions,
  );
};

export const getAllSites = async (): Promise<
  StandardResponse<SiteAllItem[]>
> => {
  return await httpRequest.get<SiteAllItem[]>(API_ENDPOINTS.common.sitesAll);
};

export const getSiteDropdownList = async (
  companyId?: number | null,
): Promise<StandardResponse<SiteDropdownItem[]>> => {
  return await httpRequest.get<SiteDropdownItem[]>(
    API_ENDPOINTS.common.siteDropdown(companyId),
  );
};

export const getCompanyConfigurationList = async (): Promise<
  StandardResponse<CommonCompanyOption[]>
> => {
  return await httpRequest.get<CommonCompanyOption[]>(
    API_ENDPOINTS.common.companyConfigurationList,
  );
};

export const getCustomerNames = async (): Promise<
  StandardResponse<string[]>
> => {
  return await httpRequest.get<string[]>(API_ENDPOINTS.common.customerName);
};

export const getMeterGroups = async (): Promise<
  StandardResponse<MeterGroupItem[]>
> => {
  return await httpRequest.get<MeterGroupItem[]>(
    API_ENDPOINTS.common.meterGroups,
  );
};

export const getSideMenu = async (): Promise<
  StandardResponse<SideMenuResponse>
> => {
  return await httpRequest.get<SideMenuResponse>(API_ENDPOINTS.common.sideMenu);
};
